// Starter faces. Each sits inside about x +-0.6, y +-0.7 of the sheet.

import type { Part, Stencil, Stroke } from '../types';
import { circlePoly, type Pt } from './geom';

export interface Preset { key: string; label: string; stencil: Stencil }

type Opts = { rotate?: number; flip?: boolean; tool?: 'saw' | 'scraper' };

function build(name: string, make: (b: Builder) => void): Stencil {
  const b = new Builder();
  make(b);
  return { name, strokes: b.strokes, parts: b.parts };
}

class Builder {
  parts: Part[] = [];
  strokes: Stroke[] = [];
  private n = 0;

  part(shape: string, x: number, y: number, scale: number, o: Opts = {}) {
    this.parts.push({ id: 'p' + ++this.n, shape, tool: o.tool ?? 'saw', x, y, scale, rotate: o.rotate ?? 0, flip: o.flip ?? false });
  }

  pair(shape: string, x: number, y: number, scale: number, o: Opts = {}) {
    this.part(shape, -x, y, scale, o);
    this.part(shape, x, y, scale, { ...o, flip: !o.flip });
  }

  line(tool: Stroke['tool'], width: number, points: Pt[], closed = false) {
    this.strokes.push({ id: 's' + ++this.n, tool, width, closed, points });
  }

  hole(x: number, y: number, r: number) {
    this.line('poker', r * 2, circlePoly(x, y, r, this.n + 7).map(p => [+p[0].toFixed(3), +p[1].toFixed(3)] as Pt), true);
  }
}

export const PRESETS: Preset[] = [
  {
    key: 'classic', label: 'Classic',
    stencil: build('Classic', b => {
      b.pair('eye-triangle', 0.28, 0.2, 0.17);
      b.part('nose-triangle', 0.02, -0.08, 0.09);
      b.part('mouth-grin', 0, -0.38, 0.42);
    }),
  },
  {
    key: 'toothy', label: 'Toothy grin',
    stencil: build('Toothy grin', b => {
      b.pair('eye-crescent', 0.3, 0.22, 0.17);
      b.part('nose-triangle', 0, -0.05, 0.08, { rotate: 0.1 });
      b.part('mouth-toothy', 0, -0.36, 0.44);
    }),
  },
  {
    key: 'wicked', label: 'Wicked',
    stencil: build('Wicked', b => {
      b.pair('eye-angry', 0.3, 0.16, 0.19);
      b.pair('brow-angry', 0.3, 0.46, 0.2);
      b.part('nose-slits', 0, -0.1, 0.09);
      b.part('mouth-fangs', 0, -0.4, 0.44);
    }),
  },
  {
    key: 'surprised', label: 'Surprised',
    stencil: build('Surprised', b => {
      b.pair('eye-round', 0.3, 0.2, 0.17);
      b.pair('brow-worried', 0.3, 0.5, 0.17, { rotate: 0.06 });
      b.part('nose-button', 0, -0.06, 0.07);
      b.part('mouth-scream', 0, -0.4, 0.21);
    }),
  },
  {
    key: 'cat', label: 'Scaredy cat',
    // a cat's head scraped into the skin, ears up; wide eyes, a little o of a mouth, whiskers cut right through
    stencil: build('Scaredy cat', b => {
      b.part('cat-head', 0, -0.02, 0.68, { tool: 'scraper' });
      b.pair('eye-triangle', 0.4, 0.46, 0.09, { rotate: 0.35 });
      b.pair('eye-round', 0.24, 0.14, 0.14);
      b.pair('brow-worried', 0.25, 0.36, 0.1, { rotate: -0.15 });
      b.part('nose-triangle', 0, -0.1, 0.055, { rotate: Math.PI });
      b.part('mouth-scream', 0, -0.32, 0.075);
      for (const s of [-1, 1]) {
        b.line('saw', 0.022, [[s * 0.15, -0.14], [s * 0.4, -0.1], [s * 0.66, -0.03]]);
        b.line('saw', 0.022, [[s * 0.15, -0.19], [s * 0.42, -0.2], [s * 0.68, -0.19]]);
        b.line('saw', 0.022, [[s * 0.14, -0.24], [s * 0.38, -0.3], [s * 0.6, -0.38]]);
      }
    }),
  },
  {
    key: 'vampire', label: 'Vampire',
    stencil: build('Vampire', b => {
      b.pair('eye-angry', 0.27, 0.18, 0.16);
      b.pair('brow-angry', 0.27, 0.42, 0.17);
      b.part('nose-triangle', 0, -0.06, 0.07);
      b.part('mouth-fangs', 0, -0.36, 0.38);
      b.part('bat', 0.46, 0.58, 0.14, { rotate: -0.2 });
      b.part('bat', -0.5, 0.52, 0.1, { rotate: 0.25, flip: true });
    }),
  },
  {
    key: 'cyclops', label: 'Cyclops',
    stencil: build('Cyclops', b => {
      b.part('eye-round', 0, 0.18, 0.27);
      b.part('nose-triangle', 0.02, -0.18, 0.08);
      b.part('mouth-toothy', 0, -0.44, 0.4);
    }),
  },
  {
    key: 'sleepy', label: 'Sleepy',
    stencil: build('Sleepy', b => {
      b.pair('eye-crescent', 0.3, 0.18, 0.15, { rotate: Math.PI });
      b.part('nose-button', 0, -0.06, 0.06);
      b.part('mouth-scream', 0.02, -0.38, 0.14);
      b.part('moon', 0.48, 0.52, 0.14);
      b.part('star', 0.28, 0.6, 0.06, { tool: 'scraper' });
    }),
  },
  {
    key: 'cute', label: 'Cute',
    stencil: build('Cute', b => {
      b.pair('eye-round', 0.3, 0.12, 0.13);
      b.part('nose-button', 0, -0.08, 0.05);
      b.part('mouth-smirk', 0, -0.28, 0.2);
      b.hole(-0.5, -0.12, 0.05);
      b.hole(0.5, -0.12, 0.05);
      b.pair('eye-star', 0.15, 0.5, 0.05, { tool: 'scraper' });
    }),
  },
  {
    key: 'ghost', label: 'Scraped ghost',
    stencil: build('Scraped ghost', b => {
      b.part('ghost', 0, 0.02, 0.58, { tool: 'scraper' });
      b.line('scraper', 0.05, [[-0.55, -0.66], [-0.3, -0.6], [-0.05, -0.68], [0.2, -0.61], [0.5, -0.67]]);
      b.line('scraper', 0.04, [[-0.4, -0.5], [-0.1, -0.46], [0.15, -0.5]]);
      b.part('star', 0.52, 0.5, 0.07);
    }),
  },
];
